import { BillingClient, RequestParams } from 'common-billing-server/generated/billingClient'
import { RequestOptions, RequestResult } from 'common/network/request-options'
import { GenericHttpError } from 'common/http/httpError'
import { jsonParse, jsonStringify } from 'common/serialization'
import { v4 } from 'uuid'

class BrowserBillingClient extends BillingClient {
    constructor(params: { baseUrl: string; apiKey?: string }) {
        super(params)
    }
    protected async _request<T>({
        method,
        url,
        body,
        optionsOverrides,
    }: {
        method: string
        url: string
        body?: any
        optionsOverrides?: Partial<RequestOptions>
    }): Promise<RequestResult<T>> {
        const headers = optionsOverrides?.headers ?? {}
        const newHeaders = Object.keys(headers).reduce(
            (acc, key) => {
                const value = headers[key]!
                acc[key] = Array.isArray(value) ? value.join(',') : value
                return acc
            },
            {} as { [key: string]: string }
        )

        const inferredHeaders = {
            'x-request-id': v4(),
            'Content-Type': 'application/json',
        }
        const requestInit: RequestInit = {
            method,
            headers: new Headers({
                ...inferredHeaders,
                ...newHeaders,
            }),
            body: JSON.stringify(body),
            credentials: 'include',
            signal: optionsOverrides?.signal,
        }

        try {
            const response: Response = await fetch(url, requestInit)
            let data = {}
            try {
                data = await response.json()
            } catch (e) {
                const contentLength = Number(response.headers.get('content-length'))
                if (!isNaN(contentLength) && contentLength > 0) {
                    //oxlint-disable-next-line no-console
                    console.log(`Failed parsing response data with expected content length: ${contentLength}`, e)
                }
                data = {}
            }
            const headers: { [key: string]: string } = {}
            for (const pair of response.headers.entries()) {
                headers[pair[0]] = pair[1]
            }
            if ((optionsOverrides?.throwOnNon200 ?? true) && (response.status < 200 || response.status > 299)) {
                throw new GenericHttpError(response.status, data, headers)
            }
            return {
                body: jsonParse(jsonStringify(data)),
                headers,
                status: response.status,
            }
        } catch (e) {
            // A superseded search aborts its requests on purpose.
            if (!optionsOverrides?.signal?.aborted) {
                // oxlint-disable-next-line no-console
                console.error(`Error: performing '${method.toUpperCase()}' towards '${url}': `, e)
            }
            throw e
        }
    }
}

export class BillingServerApi extends BrowserBillingClient {
    onRequestErrorFn: ((error: GenericHttpError) => void) | undefined = undefined

    readonly reauthenticate = (password: string) => this.reauthenticateCurrentUsers({ password })

    /** Offline standalone activation: the response for an appliance's activation request file. */
    readonly issueOfflineActivation = (request: string) => this.offlineApplianceSoftwareKeys({ request })

    readonly passkeys = {
        list: () => this.listCurrentUserPasskeys(),
        beginRegistration: () => this.getCurrentUserPasskeysOptions(),
        completeRegistration: (body: Parameters<BillingClient['passkeysCurrentUsers']>[0]) =>
            this.passkeysCurrentUsers(body),
        rename: (id: string, name: string) => this.setCurrentUserPasskeys(id, { name }),
        delete: (id: string) => this.deleteCurrentUserPasskeys(id),
        reauthenticate: this.reauthenticate,
    }

    readonly totp = {
        reauthenticate: this.reauthenticate,
        beginEnrollment: () => this.enrollmentCurrentUserTotps(),
        completeEnrollment: (enrollmentId: string, code: string) =>
            this.setCurrentUserTotpEnrollment({ enrollmentId, code }),
        remove: () => this.removeCurrentUserTotps(),
    }

    readonly totpLogin = {
        complete: (challengeId: string, code: string) => this.completeTotpLogins({ challengeId, code }),
        beginEnrollment: (challengeId: string) => this.enrollmentTotpLogins({ challengeId }),
        completeEnrollment: (enrollmentId: string, code: string) => this.setTotpLoginEnrollment({ enrollmentId, code }),
        abandon: (challengeId: string) => this.deleteTotpLoginChallenge(challengeId),
    }

    protected async _request<T>(params: RequestParams): Promise<RequestResult<T>> {
        try {
            return await super._request(params)
        } catch (genericHttpError) {
            this.onRequestErrorFn?.(genericHttpError)
            throw genericHttpError
        }
    }
}
